import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./qualifiedChat.html";
import { PUBLIC_URL } from '~/modules/config';
import { FeatureFlags } from '~/modules/feature_flags';
import { handleException } from '~/modules/utilities/error_utilities';

// eslint-disable-next-line no-var
// @ts-expect-error * Automated Type Error Fix

const QUALIFIED_COOKIE_GROUP = '3';
const QUALIFIED_CHAT_REGEX = new RegExp(QUALIFIED_COOKIE_GROUP);
const QUALIFIED_CHAT_ID = 'qualified-chat-loader';
function loadQualifiedChat() {
  // If we've already loaded the widget, do nothing
  // eslint-disable-next-line @lwc/lwc/no-document-query
  if (document.getElementById(QUALIFIED_CHAT_ID)) {
    return Promise.resolve();
  }
  const load = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = QUALIFIED_CHAT_ID;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('QUALIFIED_CHAT_LOAD_ERROR'));
    script.async = false;
    script.src = `${PUBLIC_URL}/js/appx-qualified.js`;
    document.head.appendChild(script);
  });
  const timeout = new Promise((_, reject) => setTimeout(reject, 5000, new Error('QUALIFIED_CHAT_LOAD_TIMEOUT')));
  return Promise.race([load, timeout]);
}
class QualifiedChat extends LightningElement {
  constructor(...args) {
    super(...args);
    this.chattoken = void 0;
    this.usertoken = void 0;
    this._showchatpanel = false;
    this.iframe = null;
  }
  get showchatpanel() {
    return this._showchatpanel;
  }
  set showchatpanel(value) {
    if (this._showchatpanel !== value) {
      this._showchatpanel = value;
      // Run the visibility logic whenever the property changes
      if (FeatureFlags.FF_DISABLE_AUTO_LOAD_QUALIFIED_CHAT) {
        this.handleChatPanelVisibility();
      }
    }
  }
  getIFrame() {
    this.iframe = document.body.querySelector('#q-messenger-frame');
  }
  async connectedCallback() {
    try {
      await loadQualifiedChat();
      // May the lord have mercy on our souls
      (function (w, q) {
        // eslint-disable-next-line dot-notation
        // @ts-expect-error * Automated Type Error Fix
        w.QualifiedObject = q;
        // @ts-expect-error * Automated Type Error Fix
        w[q] =
        // @ts-expect-error * Automated Type Error Fix
        w[q] ||
        // @ts-expect-error * Automated Type Error Fix
        function (args) {
          // @ts-expect-error * Automated Type Error Fix
          (w[q].q = w[q].q || []).push(args);
        };
      })(window, 'qualified');
      qualified('initialize', this.chattoken, {
        hasCookieConsent: this.isQualifiedCookieAvailable(),
        appexchangeId: this.usertoken
      });
      if (FeatureFlags.FF_DISABLE_AUTO_LOAD_QUALIFIED_CHAT) {
        qualified('open');
        this.attachObserverToChatComponent(false);
      }
    } catch (exception) {
      handleException(exception, 'qualifiedChat.initQualifiedChat');
    }
  }
  handleChatPanelVisibility() {
    if (this.iframe && this.showchatpanel) {
      qualified('open');
      setTimeout(() => {
        if (this.iframe) {
          this.iframe.classList.remove('qualified-hidden');
        }
        this.handleChatLoaded();
      }, 1000);
    } else if (this.iframe) {
      this.iframe.classList.add('qualified-hidden');
      this.handleChatLoaded();
    }
  }
  disconnectedCallback() {
    qualified('teardown');
  }
  isQualifiedCookieAvailable() {
    return window.OptanonActiveGroups?.match(QUALIFIED_CHAT_REGEX) != null;
  }
  sendAnalyticsChatInitEvent() {
    const chatInitEvent = new CustomEvent('analytics', {
      bubbles: true,
      composed: true,
      detail: {
        eventType: 'chatInit'
      }
    });
    this.dispatchEvent(chatInitEvent);
  }
  handleChatLoaded() {
    const event = new CustomEvent('chatloaded', {
      detail: {
        loading: false
      }
    });
    this.dispatchEvent(event);
  }

  /**
   * Handles the resize event of the chat component.
   *
   * This method is called by the ResizeObserver when the chat iframe is resized.
   * It checks if the iframe's width is between 0 and 100 pixels.
   * If so, We are assuming that only chat icon is being displayed when size goes below 100px.
   *
   * @param entries - An array of ResizeObserverEntry objects representing the observed elements.
   */
  onChatResized(entries) {
    if (!this.iframe) {
      this.getIFrame();
    }
    for (const entry of entries) {
      if (entry.target.clientWidth < 100 && entry.target.clientWidth > 0 && this.iframe) {
        const event = new CustomEvent('hidechat', {
          detail: {
            showQualifiedChatPanel: false
          }
        });
        this.dispatchEvent(event);
      }
    }
  }

  /**
   * Attaches a ResizeObserver to the chat component's iframe.
   *
   * This method attempts to attach a ResizeObserver to the chat component's iframe.
   * If the iframe is not immediately available, it will retry after a 1-second delay.
   *
   * @param attached - A boolean flag indicating whether the observer is already attached.
   *                   This parameter is not used in the current implementation.
   *
   * @remarks
   * - If the iframe is not available, the method will call itself recursively after a 1-second delay.
   * - The ResizeObserver, once attached, will trigger the `onChatResized` method when the iframe's size changes.
   * - This method uses a setTimeout to allow time for the iframe to be created if it doesn't exist initially.
   */
  attachObserverToChatComponent(attached) {
    if (!attached && !this.iframe) {
      setTimeout(() => {
        this.getIFrame();
        if (this.iframe) {
          const resizeObserver = new ResizeObserver(entries => this.onChatResized(entries));
          resizeObserver.observe(this.iframe);
          this.handleChatLoaded();
        } else {
          this.attachObserverToChatComponent(attached);
        }
      }, 1000);
    }
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(QualifiedChat, {
  publicProps: {
    chattoken: {
      config: 0
    },
    usertoken: {
      config: 0
    },
    showchatpanel: {
      config: 3
    }
  },
  fields: ["_showchatpanel", "iframe"]
});
const __lwc_component_class_internal = _registerComponent(QualifiedChat, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;